import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Loader, AlertTriangle, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_DAYS = ["Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function ordinal(n) {
  if (n >= 11 && n <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
}
function fmtDate(str) {
  if (!str) return "";
  const [y, mo, d] = str.split("-").map(Number);
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  return `${ordinal(d)} ${months[mo - 1]} ${y}`;
}

export default function InviteDecline() {
  const [params] = useSearchParams();
  const token = params.get("token");

  // states: loading | choice | pref_select | submitting | done_return | done_not_interested | already | error | expired
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [resultMsg, setResultMsg] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setErrorMsg("No invite token found in this link.");
      return;
    }
    loadInvite();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadInvite() {
    try {
      const { data } = await axios.get(`${API}/invite/verify?token=${token}`);
      setInfo(data);
      if (data.expired) {
        setState("expired");
        setErrorMsg("This invite link has expired — the taster session date has passed.");
      } else if (data.status === "accepted") {
        setState("already");
        setResultMsg("This invite has already been accepted.");
      } else if (data.status === "declined") {
        setState("already");
        setResultMsg("This invite has already been declined.");
      } else {
        setState("choice");
      }
    } catch (err) {
      setState("error");
      setErrorMsg(err.response?.data?.detail || "Invalid or expired invite link.");
    }
  }

  function toggleDay(day) {
    setSelectedDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return next;
    });
  }

  async function handleReturnToWL() {
    setState("submitting");
    try {
      const { data } = await axios.post(`${API}/invite/decline/return-to-wl`, {
        token,
        preferences: [...selectedDays],
      });
      setResultMsg(data.preference);
      setState("done_return");
    } catch (err) {
      setState("error");
      setErrorMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  }

  async function handleNotInterested() {
    setState("submitting");
    try {
      await axios.post(`${API}/invite/decline/not-interested`, { token });
      setState("done_not_interested");
    } catch (err) {
      setState("error");
      setErrorMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  }

  const childName = info?.child_first_name || "your child";

  return (
    <div style={{ fontFamily: "Arial, sans-serif", minHeight: "100vh", backgroundColor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", padding: "48px 40px", maxWidth: "520px", width: "100%", textAlign: "center" }}>

        {/* Logo */}
        <img
          src="https://customer-assets-jai6qajn.emergentagent.net/job_session-manager-61/artifacts/oiyq9sc2_Dynamo%20Logo%5B4147%5D.png"
          alt="Dynamo Gymnastics"
          style={{ width: "120px", marginBottom: "32px" }}
        />

        {/* Loading */}
        {state === "loading" && (
          <>
            <Loader size={40} style={{ color: "#1656a4", animation: "spin 1s linear infinite", marginBottom: "16px" }} />
            <p style={{ color: "#64748b", fontSize: "15px" }}>Loading…</p>
          </>
        )}

        {/* Submitting */}
        {state === "submitting" && (
          <>
            <Loader size={40} style={{ color: "#1656a4", animation: "spin 1s linear infinite", marginBottom: "16px" }} />
            <p style={{ color: "#64748b", fontSize: "15px" }}>Updating…</p>
          </>
        )}

        {/* Choice screen */}
        {state === "choice" && info && (
          <>
            <h1 style={{ fontSize: "20px", fontWeight: "bold", color: "#111827", margin: "0 0 8px" }}>
              Declining {childName}'s taster
            </h1>
            <p style={{ color: "#6b7280", fontSize: "14px", lineHeight: "1.6", margin: "0 0 32px" }}>
              We're sorry {childName} won't be able to make the taster on{" "}
              <strong>{fmtDate(info.taster_date)} at {info.taster_time}</strong>.
              <br />How would you like to proceed?
            </p>

            {/* Option A: Return to WL */}
            <button
              data-testid="return-to-wl-btn"
              onClick={() => setState("pref_select")}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", textAlign: "left", padding: "18px 20px",
                backgroundColor: "#eff6ff", border: "2px solid #bfdbfe",
                borderRadius: "10px", cursor: "pointer", marginBottom: "12px",
              }}
            >
              <div>
                <p style={{ margin: "0 0 4px", fontWeight: "bold", color: "#1e40af", fontSize: "15px" }}>
                  Add {childName} back to the Waiting List
                </p>
                <p style={{ margin: "0", fontSize: "13px", color: "#3b82f6" }}>
                  We'll keep {childName}'s place so we can offer another taster
                </p>
              </div>
              <ChevronRight size={20} style={{ color: "#3b82f6", flexShrink: 0 }} />
            </button>

            {/* Option B: Not interested */}
            <button
              data-testid="not-interested-btn"
              onClick={handleNotInterested}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", textAlign: "left", padding: "18px 20px",
                backgroundColor: "#fff7f7", border: "2px solid #fecaca",
                borderRadius: "10px", cursor: "pointer",
              }}
            >
              <div>
                <p style={{ margin: "0 0 4px", fontWeight: "bold", color: "#991b1b", fontSize: "15px" }}>
                  {childName} is no longer interested in attending Gymnastics Classes
                </p>
                <p style={{ margin: "0", fontSize: "13px", color: "#ef4444" }}>
                  We'll remove {childName} from our waiting list
                </p>
              </div>
              <ChevronRight size={20} style={{ color: "#ef4444", flexShrink: 0 }} />
            </button>
          </>
        )}

        {/* Preference selection */}
        {state === "pref_select" && (
          <>
            <h1 style={{ fontSize: "20px", fontWeight: "bold", color: "#111827", margin: "0 0 8px" }}>
              Preferred days for {childName}
            </h1>
            <p style={{ color: "#6b7280", fontSize: "14px", lineHeight: "1.6", margin: "0 0 6px" }}>
              Select the days that work for you.
            </p>
            <p style={{ color: "#1656a4", fontSize: "13px", fontWeight: "600", margin: "0 0 20px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px", padding: "10px 14px" }}>
              The more days you select, the quicker we can offer {childName} a place!
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center", marginBottom: "12px" }}>
              {ALL_DAYS.map(day => (
                <button
                  key={day}
                  data-testid={`day-toggle-${day.toLowerCase()}`}
                  onClick={() => toggleDay(day)}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "24px",
                    border: "2px solid",
                    borderColor: selectedDays.has(day) ? "#1656a4" : "#d1d5db",
                    backgroundColor: selectedDays.has(day) ? "#1656a4" : "#ffffff",
                    color: selectedDays.has(day) ? "#ffffff" : "#6b7280",
                    fontWeight: "600",
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {day}
                </button>
              ))}
            </div>
            {selectedDays.size === 0 && (
              <p style={{ fontSize: "13px", color: "#dc2626", margin: "0 0 16px" }}>
                Please select at least one day to continue.
              </p>
            )}
            <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "16px" }}>
              <button
                onClick={() => setState("choice")}
                style={{ padding: "11px 24px", borderRadius: "8px", border: "1px solid #d1d5db", backgroundColor: "#ffffff", color: "#374151", fontSize: "14px", cursor: "pointer" }}
              >
                Back
              </button>
              <button
                data-testid="confirm-return-btn"
                onClick={handleReturnToWL}
                disabled={selectedDays.size === 0}
                style={{
                  padding: "11px 28px", borderRadius: "8px", border: "none",
                  backgroundColor: selectedDays.size === 0 ? "#9ca3af" : "#1656a4",
                  color: "#ffffff",
                  fontWeight: "bold", fontSize: "14px",
                  cursor: selectedDays.size === 0 ? "not-allowed" : "pointer",
                }}
              >
                Confirm
              </button>
            </div>
          </>
        )}

        {/* Done — returned to WL */}
        {state === "done_return" && (
          <>
            <CheckCircle size={56} style={{ color: "#16a34a", marginBottom: "16px" }} />
            <h1 style={{ fontSize: "20px", fontWeight: "bold", color: "#111827", margin: "0 0 8px" }}>
              {childName} is back on the list!
            </h1>
            <p style={{ color: "#374151", fontSize: "15px", lineHeight: "1.6", margin: "0 0 20px" }}>
              We've added {childName} back to the waiting list with a preference for{" "}
              <strong>{resultMsg}</strong>. We'll be in touch when a space comes up.
            </p>
            <p style={{ color: "#6b7280", fontSize: "13px" }}>
              Thank you for letting us know.
            </p>
          </>
        )}

        {/* Done — not interested */}
        {state === "done_not_interested" && (
          <>
            <CheckCircle size={56} style={{ color: "#16a34a", marginBottom: "16px" }} />
            <h1 style={{ fontSize: "20px", fontWeight: "bold", color: "#111827", margin: "0 0 8px" }}>
              All done
            </h1>
            <p style={{ color: "#374151", fontSize: "15px", lineHeight: "1.6" }}>
              We've removed {childName} from our waiting list. Thank you for letting us know, and we wish {childName} all the best!
            </p>
          </>
        )}

        {/* Already processed */}
        {state === "already" && (
          <>
            <AlertTriangle size={56} style={{ color: "#f59e0b", marginBottom: "16px" }} />
            <h1 style={{ fontSize: "20px", fontWeight: "bold", color: "#111827", margin: "0 0 8px" }}>
              Already processed
            </h1>
            <p style={{ color: "#6b7280", fontSize: "14px" }}>{resultMsg}</p>
          </>
        )}

        {/* Expired */}
        {state === "expired" && (
          <>
            <AlertTriangle size={56} style={{ color: "#f59e0b", marginBottom: "16px" }} />
            <h1 style={{ fontSize: "20px", fontWeight: "bold", color: "#111827", margin: "0 0 8px" }}>
              Link Expired
            </h1>
            <p style={{ color: "#6b7280", fontSize: "14px" }}>{errorMsg}</p>
            <p style={{ color: "#6b7280", fontSize: "13px", marginTop: "12px" }}>
              Please contact us on <strong>02380 455007</strong>
            </p>
          </>
        )}

        {/* Error */}
        {state === "error" && (
          <>
            <XCircle size={56} style={{ color: "#dc2626", marginBottom: "16px" }} />
            <h1 style={{ fontSize: "20px", fontWeight: "bold", color: "#111827", margin: "0 0 8px" }}>
              Something went wrong
            </h1>
            <p style={{ color: "#6b7280", fontSize: "14px" }}>{errorMsg}</p>
            <p style={{ color: "#6b7280", fontSize: "13px", marginTop: "12px" }}>
              Please contact us on <strong>02380 455007</strong>
            </p>
          </>
        )}

        {/* Footer */}
        <p style={{ marginTop: "40px", fontSize: "11px", color: "#9ca3af", borderTop: "1px solid #f1f5f9", paddingTop: "16px" }}>
          Dynamo Gymnastics · Hamble Sports Complex · Satchell Lane, SO31 4NE
        </p>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
